import { BaseComponentImpl } from '../types/implementations';
import { ComponentLifetime, MaintenanceTemplate, TemplatesByType, getLifetimeForDirection } from '../Pages/Förvaltning/TemplatesEditor/types';
import { Building, ConditionModel, ValueWithMetadata } from '../types/protocols';
import { BaseType, MaintenanceIntervalType, BuildComponentTypeDefinition, CostUnit } from '../types/Enums';
import { formatDateOnlyForStorage } from './DateUtils';
import { Attachment } from '../types/attachments';
import { BuildingCheck, checkDisplayName, getEffectiveValues, IntervalUnit } from '../Pages/BuildingChecks/buildingChecksUtils';
import { BuildingCheckTemplate } from '../Pages/BuildingChecks/types';
import { OperatingCost, calculateYearlyCost } from '../types/operatingCosts';
import { Lease } from '../types/leases';
import { leaseIncomeForYear, leaseObjectLabel, activeMonthsInYear } from './leaseUtils';
import { ALL_FINANCIAL_CATEGORIES } from './chartColors';
import { ProjectModel, ProjectStatus, BudgetAllocation } from '../types/projects';
import { distributeAllocationOverYears } from './budgetAllocation';
import { resolveCardinalDirection } from '../Pages/Förvaltning/MaintenanceDraft/utils/cardinalDirectionResolver';
import { resolveComponentSchedule, templateLookup } from './maintenanceSchedule';

/**
 * Format cost unit for display (kr/m², kr/st, kr/lm)
 */
export function formatCostUnit(costUnit: CostUnit): string {
    switch (costUnit) {
        case 'per_sqm':
            return 'kr/m²';
        case 'per_unit':
            return 'kr/st';
        case 'per_lm':
            return 'kr/lm';
        default:
            return '';
    }
}

/**
 * Cost source type for maintenance activities and replacements
 * - 'template': Schablon — the template's unit price × quantity, computed live in a draft (default)
 * - 'customRate': Anpassad schablon — an own unit price (`customUnitPrice`) × quantity, computed live in a draft
 * - 'custom': Kostnad enligt offert — the total is stored directly (the stored name predates the UI name)
 * Both non-template sources may carry a comment and attachments.
 */
export type CostSource = 'template' | 'custom' | 'customRate';

/** UI name of a cost source */
export const COST_SOURCE_LABELS: Record<CostSource, string> = {
    template: 'Schablon',
    customRate: 'Anpassad schablon',
    custom: 'Kostnad enligt offert'
};

/** A quote (offert) is the only verified cost; both schablon variants are estimates. */
export const isQuoteCost = (source: CostSource | undefined): boolean => source === 'custom';

/** The own unit price when the source is anpassad schablon and a price is set, else undefined */
export const customRateOf = (source: CostSource | undefined, unitPrice: number | undefined): number | undefined =>
    source === 'customRate' && unitPrice !== undefined && isFinite(unitPrice) ? unitPrice : undefined;

/** The antal a per_unit activity's unit price is multiplied by: the set customUnitCount, else 1 */
export const unitCountOf = (customUnitCount: number | undefined): number =>
    customUnitCount !== undefined && isFinite(customUnitCount) && customUnitCount > 0 ? customUnitCount : 1;

/**
 * Cost category for unified cost view
 * - 'maintenance-utility': Maintenance costs for utility components (baseType = Utility)
 * - 'maintenance-other': Maintenance costs for all other components
 * - 'inspection': Inspection/building check costs
 * - 'operating-cost': Operating costs (driftkostnader)
 * - 'rental-income': Rental income (hyresintäkter) - negative costs that reduce net total
 */
// Derived from the canonical category tuple so the union and the runtime list
// (used by emptyCostsByCategory, filters, colors, labels) can never diverge.
export type CostCategory = typeof ALL_FINANCIAL_CATEGORIES[number];

/** A fresh per-category cost record with every category initialized to 0. */
export const emptyCostsByCategory = (): Record<CostCategory, number> => {
    const record = {} as Record<CostCategory, number>;
    for (const category of ALL_FINANCIAL_CATEGORIES) {
        record[category] = 0;
    }
    return record;
};

export interface MaintenanceActivity {
    nextDate: string; // Specific date in YYYY-MM-DD format
    intervalType: MaintenanceIntervalType;
    intervalValue: number;
    cost?: number; // Draft: only stored when costSource === 'custom'. Active plan: snapshot for every source
    lastUpdated: string;
    // No longer written: the draft removes an activity instead (removedActivities) and the
    // draft sync converts the flag. Readers still skip an activity that carries it.
    disabled?: boolean;

    // Cost tracking (Item 1)
    costSource?: CostSource; // undefined treated as 'template'
    customUnitPrice?: number; // Only with costSource === 'customRate', in the template's cost unit
    costComment?: string; // Only with a non-template costSource
    costAttachments?: { [key: string]: Attachment }; // PDFs, images

    // Investment tracking (Item 3)
    investmentPercentage?: number; // 0-100, undefined = 0%

    // Custom quantity for cost calculation (area m² or length lm)
    // If set, use this instead of component's totalArea/linearMeters
    customQuantity?: number;

    // Antal for a per_unit (kr/st) template: the cost is unit price × antal, absent = 1.
    // A calculation multiplier set in the draft only; never the component's or building's count.
    customUnitCount?: number;

    // Free-text note about the activity, set in the draft (absent, never empty).
    // Kept across cycles and pre-filled into a work order created from the activity.
    comment?: string;

    // Completion tracking — written by the reschedule-on-completion trigger
    // (mf-functions) when a work order created from this activity is completed.
    lastCompleted?: string;             // YYYY-MM-DD — completion date the schedule was advanced from
    lastCompletedBy?: string;           // UID of the completing user (never name/email)
    lastCompletedWorkOrderKey?: string; // Work order that last advanced this activity (idempotency guard)
}

export interface ComponentMaintenanceData {
    replacementYear?: number;
    replacementCost?: number; // Draft: only stored when replacementCostSource === 'custom'
    maintenanceActivities: {
        [templateId: string]: MaintenanceActivity;
    };
    // Type templates the user removed from this component: they have no entry in
    // maintenanceActivities and are not re-created or scheduled. Written in the
    // draft and kept at activation; absent, not empty, when nothing is removed.
    removedActivities?: { [templateId: string]: true };
    excluded?: boolean;
    isDone?: boolean; // Used in draft workflow to mark completed components

    // Replacement cost tracking (Item 4)
    replacementCostSource?: CostSource; // undefined treated as 'template'
    replacementCustomUnitPrice?: number; // Only with replacementCostSource === 'customRate', in the lifetime's cost unit
    // Antal for a per_unit (kr/st) lifetime: the replacement costs unit price × antal, absent = 1.
    // A calculation multiplier set in the draft only (e.g. a stam priced per lägenhet); never the component's count.
    replacementCustomUnitCount?: number;
    replacementCostComment?: string; // Only with a non-template replacementCostSource
    replacementAttachments?: { [key: string]: Attachment }; // PDFs, images
    replacementInvestmentPercentage?: number; // 0-100, undefined = 0%
}

export interface MaintenancePlan {
    components: {
        [componentKey: string]: ComponentMaintenanceData;
    };
    metadata: {
        savedAt: string;
        buildingKey: string;
        
        // Plan version and activation tracking
        version?: string;
        activatedAt?: string;
        activatedBy?: string; // UID
        priceYear?: number;
        createdBy?: string; // UID
    };
}

export interface YearlyActivity {
    year: number;
    activities: {
        componentKey: string;
        componentName: string;
        componentType: string;
        buildingKey?: string;
        buildingName?: string;
        activityType: 'replacement' | 'maintenance' | 'inspection' | 'operating-cost' | 'project';
        activityName: string;
        templateId?: string;
        frequency: number;
        frequencyType: MaintenanceIntervalType;
        cost: number;
        costCalculation: string;
        baseType: string;
        nextDate?: string; // YYYY-MM-DD format
        // Cost source info for tooltips
        costSource?: CostSource;
        costComment?: string;
        // The cost has files attached (an offert, a price list)
        hasCostAttachments?: boolean;
        // Investment percentage (0-100)
        investmentPercentage?: number;
        // The maintenance row's free-text comment
        comment?: string;
        // Inflation adjustment properties
        originalCost?: number;
        inflationYears?: number;
        inflationMultiplier?: number;
        priceSettingYear?: number;
        // Unified cost view fields
        costCategory?: CostCategory;
        operatingCostKey?: string;  // For operating cost navigation/editing
        inspectionKey?: string;     // For inspection navigation (check key in inspections/active/{b})
        leaseKey?: string;          // For lease income rows (leases/{key})
    }[];
    totalCost: number;
    costsByBaseType: Record<string, number>;
    costsByCategory?: Record<CostCategory, number>;  // For unified cost view
}

// Single source of truth for the activity-type union. Re-export and reuse this
// rather than re-declaring the union locally (a prior duplication broke the build).
export type ActivityType = YearlyActivity['activities'][number]['activityType'];

export interface GenerateYearlyPlansOptions {
    building: Building;
    plan: MaintenancePlan;
    componentLifetimes: Record<string, ComponentLifetime>;
    maintenanceTemplates: TemplatesByType;
    /** Optional component conditions to merge into building components */
    componentConditions?: Record<string, ValueWithMetadata<ConditionModel>>;
    yearsToShow: number;
    buildingName?: string;
    buildComponentTypes: Record<string, BuildComponentTypeDefinition>;
    /** Only include components where isDone === true (for draft preview) */
    filterIsDone?: boolean;
    /** Include empty years in output (default: true, years with no activities are included) */
    includeEmptyYears?: boolean;
    /**
     * The plan is a draft: template costs are computed live and the type's
     * templates without an entry are scheduled with defaults. Default false —
     * an active plan is read as it was activated (snapshotted costs, listed
     * activities only).
     */
    isDraft?: boolean;
}

/**
 * Calculate cost from template for a component
 * Used for display and activation snapshot
 *
 * @param component - The component to calculate cost for
 * @param template - The maintenance template with cost configuration
 * @param building - Building data for area calculations
 * @param customQuantity - Optional custom area/length override. If provided and > 0, uses this instead of component values.
 * @param unitPrice - Optional own unit price (anpassad schablon) used instead of the template's.
 * @param customUnitCount - Optional antal for a per_unit template. If provided and > 0, multiplies the unit price.
 */
export function calculateActivityCost(
    component: BaseComponentImpl,
    template: MaintenanceTemplate,
    building: Building,
    customQuantity?: number,
    unitPrice?: number,
    customUnitCount?: number
): number {
    const rate = unitPrice ?? template.cost;
    if (!rate) return 0;

    if (template.cost_unit === 'per_sqm') {
        // Use custom quantity if provided and valid, otherwise calculate from component
        const area = (customQuantity !== undefined && customQuantity > 0)
            ? customQuantity
            : component.totalArea(building);
        if (area === null || area === 0) return 0;
        return Math.round(rate * area);
    } else if (template.cost_unit === 'per_lm') {
        // Use custom quantity if provided and valid, otherwise use component linear meters
        const length = (customQuantity !== undefined && customQuantity > 0)
            ? customQuantity
            : component.linearMeters?.value;
        if (!length || length === 0) return 0;
        return Math.round(rate * length);
    } else {
        // per_unit - unit price × antal (customQuantity not applicable)
        return Math.round(rate * unitCountOf(customUnitCount));
    }
}

/**
 * Get display cost for an activity (handles both template and custom sources)
 * In draft: template costs are calculated dynamically
 * In active: costs are always static (snapshotted at activation)
 *
 * Priority: offert cost > active snapshot > live (own or template unit price × custom quantity or component area)
 */
export function getActivityDisplayCost(
    activity: MaintenanceActivity,
    component: BaseComponentImpl,
    template: MaintenanceTemplate,
    building: Building,
    isDraft: boolean = true
): number {
    // Custom costs are always stored directly (highest priority)
    if (activity.costSource === 'custom' && activity.cost !== undefined) {
        return activity.cost;
    }

    // For active plans, use stored cost (snapshotted at activation)
    if (!isDraft && activity.cost !== undefined) {
        return activity.cost;
    }

    // For draft with a schablon source, calculate dynamically
    // Use custom quantity and own unit price if available
    return calculateActivityCost(
        component, template, building, activity.customQuantity,
        customRateOf(activity.costSource, activity.customUnitPrice),
        activity.customUnitCount
    );
}

/**
 * Calculate replacement cost from template for a component
 */
export function calculateReplacementCost(
    component: BaseComponentImpl,
    lifetime: ComponentLifetime,
    building: Building,
    unitPrice?: number,
    customUnitCount?: number
): number {
    // Direction is inherited (a window takes its facade's), so a south-facing
    // window gets the south lifetime's cost like every other reader
    const directionalLifetime = getLifetimeForDirection(
        lifetime,
        resolveCardinalDirection(component, building)
    );

    const cost = unitPrice ?? (directionalLifetime.replacementCost || 0);

    if (lifetime.costUnit === 'per_sqm') {
        const area = component.totalArea(building);
        if (area === null || area === 0) return 0;
        return Math.round(cost * area);
    } else if (lifetime.costUnit === 'per_lm') {
        const length = component.linearMeters?.value;
        if (!length || length === 0) return 0;
        return Math.round(cost * length);
    } else {
        // per_unit - unit price × antal
        return Math.round(cost * unitCountOf(customUnitCount));
    }
}

export interface ReplacementQuantityDisplay {
    text: string;
    /** The quantity does not drive the cost (kostnad enligt offert) */
    muted: boolean;
    title?: string;
}

/**
 * The Mängd of a replacement: the component's area or length, or the antal for kr/st
 * (absent = 1 st). Under kostnad enligt offert it is shown muted, since it no
 * longer drives the cost.
 */
export function replacementQuantityDisplay(
    costUnit: CostUnit | undefined,
    area: number | null,
    length: number | null | undefined,
    costSource: CostSource | undefined,
    customUnitCount?: number
): ReplacementQuantityDisplay {
    const muted = costSource === 'custom';
    const title = muted ? 'Kostnad enligt offert används' : undefined;

    if (costUnit === 'per_sqm') {
        if (area === null) return { text: '–', muted: true, title: 'Area saknas' };
        return { text: `${area.toLocaleString('sv-SE')} m²`, muted, title };
    }
    if (costUnit === 'per_lm') {
        if (!length) return { text: '–', muted: true, title: 'Längd saknas' };
        return { text: `${length.toLocaleString('sv-SE')} lm`, muted, title };
    }
    return { text: `${unitCountOf(customUnitCount).toLocaleString('sv-SE')} st`, muted, title };
}

/**
 * Get display replacement cost for a component (handles both template and custom sources)
 */
export function getReplacementDisplayCost(
    componentData: ComponentMaintenanceData,
    component: BaseComponentImpl,
    lifetime: ComponentLifetime,
    building: Building,
    isDraft: boolean = true
): number {
    // Custom costs are always stored directly
    if (componentData.replacementCostSource === 'custom' && componentData.replacementCost !== undefined) {
        return componentData.replacementCost;
    }

    // For active plans, use stored cost (snapshotted at activation)
    if (!isDraft && componentData.replacementCost !== undefined) {
        return componentData.replacementCost;
    }

    // For draft with a schablon source, calculate dynamically
    return calculateReplacementCost(
        component, lifetime, building,
        customRateOf(componentData.replacementCostSource, componentData.replacementCustomUnitPrice),
        componentData.replacementCustomUnitCount
    );
}

/**
 * Convert interval to days
 */
export function intervalToDays(intervalType: MaintenanceIntervalType, intervalValue: number): number {
    // Validate input
    if (intervalValue <= 0 || !isFinite(intervalValue)) {
        console.warn('Invalid interval value:', intervalValue);
        return 0;
    }

    switch (intervalType) {
        case MaintenanceIntervalType.days:
            return intervalValue;
        case MaintenanceIntervalType.weeks:
            return intervalValue * 7;
        case MaintenanceIntervalType.months:
            return intervalValue * 30.44; // Average days per month
        case MaintenanceIntervalType.years:
            return intervalValue * 365.25; // Account for leap years
        default:
            console.warn('Unknown interval type:', intervalType);
            return intervalValue * 365.25;
    }
}

/**
 * Add interval to a date
 */
export function addIntervalToDate(date: Date, intervalType: MaintenanceIntervalType, intervalValue: number): Date {
    // Validate inputs
    if (isNaN(date.getTime())) {
        throw new Error('Invalid date provided to addIntervalToDate');
    }
    if (intervalValue <= 0 || !isFinite(intervalValue)) {
        throw new Error(`Interval value must be positive, got: ${intervalValue}`);
    }

    const newDate = new Date(date);

    switch (intervalType) {
        case MaintenanceIntervalType.days:
            newDate.setDate(newDate.getDate() + intervalValue);
            break;
        case MaintenanceIntervalType.weeks:
            newDate.setDate(newDate.getDate() + (intervalValue * 7));
            break;
        case MaintenanceIntervalType.months:
            newDate.setMonth(newDate.getMonth() + intervalValue);
            break;
        case MaintenanceIntervalType.years:
            newDate.setFullYear(newDate.getFullYear() + intervalValue);
            break;
        default:
            throw new Error(`Unknown interval type: ${intervalType}`);
    }

    // Validate result
    if (isNaN(newDate.getTime())) {
        throw new Error('Resulting date is invalid after adding interval');
    }

    return newDate;
}

/**
 * Format date to YYYY-MM-DD string using Stockholm timezone
 * This ensures consistent date formatting across the application
 */
export function formatDateToString(date: Date): string {
    if (isNaN(date.getTime())) {
        throw new Error('Invalid date provided to formatDateToString');
    }
    // Use the shared DateUtils function for consistent timezone handling
    return formatDateOnlyForStorage(date);
}

/**
 * Parse date from YYYY-MM-DD string
 * Creates a Date object in Stockholm timezone
 */
export function parseDateFromString(dateString: string): Date {
    if (!dateString || typeof dateString !== 'string') {
        throw new Error('Invalid date string provided to parseDateFromString');
    }

    // Validate format (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateString)) {
        throw new Error(`Invalid date format. Expected YYYY-MM-DD, got: ${dateString}`);
    }

    const [year, month, day] = dateString.split('-').map(Number);
    
    // Validate date components
    if (year < 1900 || year > 2100) {
        throw new Error(`Invalid year: ${year}`);
    }
    if (month < 1 || month > 12) {
        throw new Error(`Invalid month: ${month}`);
    }
    if (day < 1 || day > 31) {
        throw new Error(`Invalid day: ${day}`);
    }

    // Create date in Stockholm timezone (local timezone, but we're in Sweden)
    // The date string is assumed to be in Stockholm timezone already
    const date = new Date(year, month - 1, day);
    
    if (isNaN(date.getTime())) {
        throw new Error(`Invalid date: ${dateString}`);
    }

    return date;
}

/**
 * Get next maintenance date based on current date and interval
 */
export function getNextMaintenanceDate(
    currentDate: string,
    intervalType: MaintenanceIntervalType,
    intervalValue: number
): string {
    const date = parseDateFromString(currentDate);
    const nextDate = addIntervalToDate(date, intervalType, intervalValue);
    return formatDateToString(nextDate);
}

/**
 * Translate interval type to Swedish
 */
export function translateIntervalType(intervalType: MaintenanceIntervalType): string {
    switch (intervalType) {
        case MaintenanceIntervalType.days:
            return 'dagar';
        case MaintenanceIntervalType.weeks:
            return 'veckor';
        case MaintenanceIntervalType.months:
            return 'månader';
        case MaintenanceIntervalType.years:
            return 'år';
        default:
            return 'år';
    }
}

/**
 * Interface for activities that can be sorted by date
 */
export interface ActivityForSorting {
    nextDate?: string;
    activityName: string;
}

/**
 * Sort activities by date, then by name as fallback
 */
export function sortActivitiesByDate<T extends ActivityForSorting>(activities: T[]): T[] {
    return [...activities].sort((a, b) => {
        // Sort by nextDate if available, otherwise by activity name
        if (a.nextDate && b.nextDate) {
            return a.nextDate.localeCompare(b.nextDate);
        }
        if (a.nextDate && !b.nextDate) return -1;
        if (!a.nextDate && b.nextDate) return 1;
        return (a.activityName || '').localeCompare(b.activityName || '');
    });
}

/**
 * Generate yearly maintenance plans for a single building
 */
export const generateYearlyPlansForBuilding = (options: GenerateYearlyPlansOptions): YearlyActivity[] => {
    const {
        building,
        plan,
        componentLifetimes,
        maintenanceTemplates,
        componentConditions,
        yearsToShow,
        buildingName,
        buildComponentTypes,
        isDraft = false
    } = options;

    if (!building?.model?.structure?.components || !plan || !plan.components || typeof plan.components !== 'object' || !componentLifetimes || !maintenanceTemplates) {
        return [];
    }

    const currentYear = new Date().getFullYear();
    const endYear = currentYear + yearsToShow;
    const yearlyPlans: YearlyActivity[] = [];

    // Initialize years
    for (let year = currentYear; year < endYear; year++) {
        yearlyPlans.push({
            year,
            activities: [],
            totalCost: 0,
            costsByBaseType: {}
        });
    }

    const findTemplate = templateLookup(maintenanceTemplates);

    const addActivity = (year: number, activity: YearlyActivity['activities'][number]): void => {
        const yearPlan = yearlyPlans[year - currentYear];
        if (!yearPlan) return;
        yearPlan.activities.push(activity);
        yearPlan.totalCost += activity.cost;
        yearPlan.costsByBaseType[activity.baseType] = (yearPlan.costsByBaseType[activity.baseType] || 0) + activity.cost;
    };

    // Process each component
    Object.values(building.model.structure.components || {}).forEach(componentData => {
        if (!componentData.specificType?.value) return;

        const componentKey = componentData.key;
        const componentActive = plan.components[componentKey];
        if (!componentActive) return;

        // Skip excluded components
        if (componentActive.excluded === true) return;

        // Skip components that are not done (when filterIsDone is enabled)
        if (options.filterIsDone && componentActive.isDone !== true) return;

        // A newer condition replaces the building's without mutating it
        const condition = componentConditions?.[componentKey];
        const component = new BaseComponentImpl(condition ? { ...componentData, condition } : componentData);
        const componentName = component.displayName(building);
        const componentType = component.specificType?.value;
        if (!componentType) return;

        const schedule = resolveComponentSchedule({
            component,
            entry: componentActive,
            building,
            lifetime: componentLifetimes[componentType],
            typeTemplates: maintenanceTemplates[componentType] || [],
            findTemplate,
            window: { startYear: currentYear, endYear },
            isDraft
        });
        if (!schedule) return;

        const baseType = buildComponentTypes[componentType]?.baseType || BaseType.Utility;
        const baseTypeKey = baseType.toString();
        const costCategory: CostCategory = baseType === BaseType.Utility
            ? 'maintenance-utility'
            : 'maintenance-other';

        const { replacement } = schedule;
        if (replacement) {
            replacement.years.forEach(replacementYear => {
                addActivity(replacementYear, {
                    componentKey,
                    componentName,
                    componentType,
                    buildingKey: building.key,
                    buildingName,
                    activityType: 'replacement',
                    activityName: `Ersätt ${componentName}`,
                    frequency: replacement.expectedLifetimeYears,
                    frequencyType: MaintenanceIntervalType.years,
                    cost: replacement.cost,
                    costCalculation: replacement.costCalculation,
                    baseType: baseTypeKey,
                    nextDate: `${replacementYear}-01-01`,
                    costSource: replacement.costSource,
                    costComment: replacement.costComment,
                    hasCostAttachments: Object.keys(componentActive.replacementAttachments ?? {}).length > 0,
                    investmentPercentage: replacement.investmentPercentage,
                    costCategory
                });
            });
        }

        schedule.activities.forEach(activity => {
            activity.dates.forEach(date => {
                addActivity(Number(date.slice(0, 4)), {
                    componentKey,
                    componentName,
                    componentType,
                    buildingKey: building.key,
                    buildingName,
                    activityType: 'maintenance',
                    activityName: activity.template.name,
                    templateId: activity.templateId,
                    frequency: activity.intervalValue,
                    frequencyType: activity.intervalType,
                    cost: activity.cost,
                    costCalculation: activity.costCalculation,
                    baseType: baseTypeKey,
                    nextDate: date,
                    costSource: activity.costSource,
                    hasCostAttachments: Object.keys(componentActive.maintenanceActivities?.[activity.templateId]?.costAttachments ?? {}).length > 0,
                    costComment: activity.costComment,
                    investmentPercentage: activity.investmentPercentage,
                    comment: activity.comment,
                    costCategory
                });
            });
        });
    });

    return yearlyPlans;
};

/**
 * Aggregate yearly plans from multiple buildings
 */
export const aggregateYearlyPlans = (buildingPlans: YearlyActivity[][]): YearlyActivity[] => {
    if (buildingPlans.length === 0) return [];

    // Get the year range from the first building's plans
    const firstBuilding = buildingPlans[0];
    if (firstBuilding.length === 0) return [];

    const aggregatedPlans: YearlyActivity[] = [];

    // Initialize aggregated plans with the same year structure
    firstBuilding.forEach(yearPlan => {
        aggregatedPlans.push({
            year: yearPlan.year,
            activities: [],
            totalCost: 0,
            costsByBaseType: {}
        });
    });

    // Aggregate data from all buildings
    buildingPlans.forEach(buildingYearlyPlans => {
        buildingYearlyPlans.forEach((yearPlan, yearIndex) => {
            if (yearIndex < aggregatedPlans.length) {
                // Add activities
                aggregatedPlans[yearIndex].activities.push(...yearPlan.activities);

                // Add to total cost
                aggregatedPlans[yearIndex].totalCost += yearPlan.totalCost;

                // Aggregate costs by base type
                Object.entries(yearPlan.costsByBaseType).forEach(([baseType, cost]) => {
                    if (!aggregatedPlans[yearIndex].costsByBaseType[baseType]) {
                        aggregatedPlans[yearIndex].costsByBaseType[baseType] = 0;
                    }
                    aggregatedPlans[yearIndex].costsByBaseType[baseType] += cost;
                });
            }
        });
    });

    return aggregatedPlans;
};

/**
 * Generate inspection cost projections for a building
 * Only includes inspections where effective cost > 0
 */
export const generateInspectionCostProjections = (
    checks: (BuildingCheck & { checkKey: string })[],
    templates: Record<string, BuildingCheckTemplate>,
    buildingKey: string,
    buildingName: string,
    yearsToProject: number
): YearlyActivity[] => {
    const currentYear = new Date().getFullYear();
    const endYear = currentYear + yearsToProject;
    const yearlyPlans: YearlyActivity[] = [];

    // Initialize years
    for (let year = currentYear; year < endYear; year++) {
        yearlyPlans.push({
            year,
            activities: [],
            totalCost: 0,
            costsByBaseType: {},
            costsByCategory: emptyCostsByCategory()
        });
    }

    // Process each check
    checks.forEach(check => {
        const template = templates[check.templateId];
        if (!template) return;

        const effective = getEffectiveValues(check, template);
        const effectiveCost = effective.cost;
        const name = checkDisplayName(check, template.name);

        // Only include if cost > 0
        if (effectiveCost <= 0) return;

        const interval = effective.interval;

        // Parse the next due date and project forward
        let nextDueDate = new Date(check.nextDueDate);

        // Safety limit
        const MAX_ITERATIONS = 200;
        let iterations = 0;

        while (nextDueDate.getFullYear() < endYear && iterations < MAX_ITERATIONS) {
            iterations++;
            const inspectionYear = nextDueDate.getFullYear();
            const yearIndex = inspectionYear - currentYear;

            if (yearIndex >= 0 && yearIndex < yearlyPlans.length) {
                yearlyPlans[yearIndex].activities.push({
                    componentKey: '',
                    componentName: name,
                    componentType: 'inspection',
                    buildingKey,
                    buildingName,
                    activityType: 'inspection',
                    activityName: name,
                    templateId: check.templateId,
                    frequency: interval.value,
                    frequencyType: mapIntervalUnit(interval.unit),
                    cost: effectiveCost,
                    costCalculation: `${effectiveCost.toLocaleString('sv-SE')} kr`,
                    baseType: 'inspection',
                    nextDate: formatDateToString(nextDueDate),
                    costCategory: 'inspection',
                    inspectionKey: check.checkKey
                });

                yearlyPlans[yearIndex].totalCost += effectiveCost;
                yearlyPlans[yearIndex].costsByCategory!['inspection'] += effectiveCost;
            }

            // Move to next occurrence
            nextDueDate = addIntervalToDateGeneric(nextDueDate, interval.unit, interval.value);
        }
    });

    return yearlyPlans;
};

/**
 * Generate operating cost projections for a building
 * Operating costs are shown as one annualized line item per year
 */
export const generateOperatingCostProjections = (
    costs: OperatingCost[],
    buildingKey: string,
    buildingName: string,
    yearsToProject: number,
    categoryLabels: Record<string, string> = {}
): YearlyActivity[] => {
    const currentYear = new Date().getFullYear();
    const endYear = currentYear + yearsToProject;
    const yearlyPlans: YearlyActivity[] = [];

    // Initialize years
    for (let year = currentYear; year < endYear; year++) {
        yearlyPlans.push({
            year,
            activities: [],
            totalCost: 0,
            costsByBaseType: {},
            costsByCategory: emptyCostsByCategory()
        });
    }

    // Add each operating cost to each year (annualized)
    costs.forEach(cost => {
        const yearlyCost = calculateYearlyCost(cost.amount, cost.interval);
        if (yearlyCost <= 0) return;

        // Build activity name from category label and optional description
        const categoryLabel = categoryLabels[cost.categoryKey] || 'Driftkostnad';
        const activityName = cost.description
            ? `${categoryLabel}: ${cost.description}`
            : categoryLabel;

        // Add to each year
        for (let yearIndex = 0; yearIndex < yearlyPlans.length; yearIndex++) {
            yearlyPlans[yearIndex].activities.push({
                componentKey: '',
                componentName: activityName,
                componentType: 'operating-cost',
                buildingKey,
                buildingName,
                activityType: 'operating-cost',
                activityName: activityName,
                frequency: cost.interval.count,
                frequencyType: cost.interval.unit === 'month'
                    ? MaintenanceIntervalType.months
                    : MaintenanceIntervalType.years,
                cost: yearlyCost,
                costCalculation: `${cost.amount.toLocaleString('sv-SE')} kr ${formatIntervalSwedish(cost.interval)} = ${Math.round(yearlyCost).toLocaleString('sv-SE')} kr/år`,
                baseType: 'operating-cost',
                costCategory: 'operating-cost',
                operatingCostKey: cost.key
            });

            yearlyPlans[yearIndex].totalCost += yearlyCost;
            yearlyPlans[yearIndex].costsByCategory!['operating-cost'] += yearlyCost;
        }
    });

    return yearlyPlans;
};

/**
 * Generate lease income projections for a building.
 * Income is one line per lease and year, prorated by the months the lease is
 * active in that year (parking-tenants plan D9), stored as NEGATIVE cost so
 * it subtracts from the total. Category string 'rental-income' is kept so
 * chart colours, labels and saved filters keep working.
 */
export const generateLeaseIncomeProjections = (
    leases: Lease[],
    tenantNames: Record<string, string>,
    buildingKey: string,
    buildingName: string,
    yearsToProject: number
): YearlyActivity[] => {
    const currentYear = new Date().getFullYear();
    const endYear = currentYear + yearsToProject;
    const yearlyPlans: YearlyActivity[] = [];

    // Initialize years
    for (let year = currentYear; year < endYear; year++) {
        yearlyPlans.push({
            year,
            activities: [],
            totalCost: 0,
            costsByBaseType: {},
            costsByCategory: emptyCostsByCategory()
        });
    }

    leases.forEach(lease => {
        const tenantName = tenantNames[lease.tenantKey] || 'Okänd person';
        const activityName = `${tenantName}: ${leaseObjectLabel(lease)}`;

        yearlyPlans.forEach(plan => {
            const months = activeMonthsInYear(lease, plan.year);
            const income = leaseIncomeForYear(lease, plan.year);
            if (months === 0 || income <= 0) return;

            plan.activities.push({
                componentKey: '',
                componentName: activityName,
                componentType: 'rental-income',
                buildingKey,
                buildingName,
                activityType: 'operating-cost', // Use operating-cost type for display purposes
                activityName,
                frequency: lease.interval.count,
                frequencyType: lease.interval.unit === 'month'
                    ? MaintenanceIntervalType.months
                    : MaintenanceIntervalType.years,
                cost: -income, // NEGATIVE to subtract from costs
                costCalculation: formatLeaseIncomeCalculation(lease.amount, lease.interval, months),
                baseType: 'rental-income',
                costCategory: 'rental-income',
                leaseKey: lease.key
            });

            plan.totalCost -= income;
            plan.costsByCategory!['rental-income'] -= income;
        });
    });

    return yearlyPlans;
};

/**
 * Maps each project budget bucket to its chart category and investment share.
 * Maintenance buckets are never investment (0%); the investment bucket is 100%.
 */
const PROJECT_BUDGET_CATEGORIES: {
    key: keyof BudgetAllocation;
    category: CostCategory;
    investmentPercentage: number;
}[] = [
    { key: 'investmentAmount', category: 'project-investment', investmentPercentage: 100 },
    { key: 'buildingMaintenanceAmount', category: 'maintenance-other', investmentPercentage: 0 },
    { key: 'propertyTechMaintenanceAmount', category: 'maintenance-utility', investmentPercentage: 0 }
];

/** Parse the 4-digit year prefix of a YYYY-MM-DD date; null when unparseable. */
const parseProjectYear = (date?: string): number | null => {
    if (!date) return null;
    const year = parseInt(date.slice(0, 4), 10);
    return Number.isFinite(year) && year > 1900 ? year : null;
};

/**
 * Generate project budget projections for the Ekonomi forecast.
 *
 * Each project's budget is distributed equally across the calendar years it
 * spans (startDate → plannedEndDate, or actualEndDate when COMPLETED), and each
 * bucket is emitted as its own activity mapped to a cost category. The returned
 * array always covers the full projection window (one entry per year) so it
 * aligns positionally with the other generators in aggregateYearlyPlans.
 */
export const generateProjectCostProjections = (
    projects: Array<ProjectModel & { buildingName?: string }>,
    yearsToProject: number
): YearlyActivity[] => {
    const currentYear = new Date().getFullYear();
    const endYear = currentYear + yearsToProject;
    const yearlyPlans: YearlyActivity[] = [];

    // Initialize the full window so the array is positionally comparable
    for (let year = currentYear; year < endYear; year++) {
        yearlyPlans.push({
            year,
            activities: [],
            totalCost: 0,
            costsByBaseType: {},
            costsByCategory: emptyCostsByCategory()
        });
    }

    projects.forEach(project => {
        const budget = project.budget;
        if (!budget || typeof budget !== 'object') return; // skip unset or legacy/malformed budgets

        const startYear = parseProjectYear(project.startDate);
        if (startYear === null) return; // can't place a project with no parseable start

        const rawEnd = project.status === ProjectStatus.COMPLETED && project.actualEndDate
            ? project.actualEndDate
            : project.plannedEndDate;
        const endYearOfProject = parseProjectYear(rawEnd) ?? startYear;

        const perYear = distributeAllocationOverYears(budget, startYear, endYearOfProject);
        const spanEnd = Math.max(startYear, endYearOfProject);
        const buildingName = project.buildingName || 'Namnlös byggnad';

        for (let yearIndex = 0; yearIndex < yearlyPlans.length; yearIndex++) {
            const { year } = yearlyPlans[yearIndex];
            if (year < startYear || year > spanEnd) continue; // outside the project's span

            PROJECT_BUDGET_CATEGORIES.forEach(({ key, category, investmentPercentage }) => {
                const cost = perYear[key];
                if (!(cost > 0)) return; // excludes 0, negatives, and NaN

                yearlyPlans[yearIndex].activities.push({
                    componentKey: '',
                    componentName: project.title,
                    componentType: 'project',
                    buildingKey: project.buildingKey,
                    buildingName,
                    activityType: 'project',
                    activityName: `Projekt: ${project.title}`,
                    frequency: 1,
                    frequencyType: MaintenanceIntervalType.years,
                    cost,
                    costCalculation: `${Math.round(budget[key]).toLocaleString('sv-SE')} kr fördelat över ${spanEnd - startYear + 1} år`,
                    baseType: 'project',
                    costCategory: category,
                    investmentPercentage
                });

                yearlyPlans[yearIndex].totalCost += cost;
                yearlyPlans[yearIndex].costsByCategory![category] += cost;
            });
        }
    });

    return yearlyPlans;
};

/**
 * Helper to format lease income calculation as "12 × 7 500 kr" or, for a
 * partial year, "7 500 kr/mån × 10 mån".
 */
const formatLeaseIncomeCalculation = (
    amount: number,
    interval: { count: number; unit: 'month' | 'year' },
    activeMonths: number
): string => {
    const { count, unit } = interval;
    const amountText = `${amount.toLocaleString('sv-SE')} kr`;
    if (activeMonths < 12) {
        return unit === 'month'
            ? `${amountText}/${count === 1 ? 'mån' : `${count} mån`} × ${activeMonths} mån`
            : `${amountText}/${count === 1 ? 'år' : `${count} år`} × ${activeMonths}/12`;
    }
    const timesPerYear = unit === 'month' ? Math.floor(12 / count) : Math.floor(1 / count);
    if (timesPerYear === 1) {
        return `${amountText}/år`;
    }
    return `${timesPerYear} × ${amountText}`;
};

/**
 * Helper to map inspection interval unit to MaintenanceIntervalType
 */
const mapIntervalUnit = (unit: IntervalUnit): MaintenanceIntervalType => {
    switch (unit) {
        case 'days': return MaintenanceIntervalType.days;
        case 'weeks': return MaintenanceIntervalType.weeks;
        case 'months': return MaintenanceIntervalType.months;
        case 'years': return MaintenanceIntervalType.years;
        default: return MaintenanceIntervalType.years;
    }
};

/**
 * Helper to add interval to date (generic version for inspection intervals)
 */
const addIntervalToDateGeneric = (date: Date, unit: IntervalUnit, value: number): Date => {
    const newDate = new Date(date);
    switch (unit) {
        case 'days':
            newDate.setDate(newDate.getDate() + value);
            break;
        case 'weeks':
            newDate.setDate(newDate.getDate() + (value * 7));
            break;
        case 'months':
            newDate.setMonth(newDate.getMonth() + value);
            break;
        case 'years':
            newDate.setFullYear(newDate.getFullYear() + value);
            break;
    }
    return newDate;
};

/**
 * Helper to format operating cost interval as Swedish text
 */
const formatIntervalSwedish = (interval: { count: number; unit: 'month' | 'year' }): string => {
    const { count, unit } = interval;
    if (unit === 'month') {
        if (count === 1) return 'per månad';
        if (count === 3) return 'per kvartal';
        if (count === 6) return 'per halvår';
        return `var ${count}:e månad`;
    } else {
        if (count === 1) return 'per år';
        return `var ${count}:e år`;
    }
};

/**
 * Merge multiple yearly plan arrays (for combining maintenance + inspection + operating costs)
 */
export const mergeYearlyPlans = (...planArrays: YearlyActivity[][]): YearlyActivity[] => {
    if (planArrays.length === 0) return [];

    // Find the common year range
    const allYears = new Set<number>();
    planArrays.forEach(plans => {
        plans.forEach(plan => allYears.add(plan.year));
    });

    if (allYears.size === 0) return [];

    const sortedYears = Array.from(allYears).sort((a, b) => a - b);
    const mergedPlans: YearlyActivity[] = sortedYears.map(year => ({
        year,
        activities: [],
        totalCost: 0,
        costsByBaseType: {},
        costsByCategory: emptyCostsByCategory()
    }));

    // Merge all plans
    planArrays.forEach(plans => {
        plans.forEach(plan => {
            const mergedPlan = mergedPlans.find(p => p.year === plan.year);
            if (!mergedPlan) return;

            // Merge activities
            mergedPlan.activities.push(...plan.activities);

            // Sum total cost
            mergedPlan.totalCost += plan.totalCost;

            // Merge costsByBaseType
            Object.entries(plan.costsByBaseType).forEach(([baseType, cost]) => {
                if (!mergedPlan.costsByBaseType[baseType]) {
                    mergedPlan.costsByBaseType[baseType] = 0;
                }
                mergedPlan.costsByBaseType[baseType] += cost;
            });

            // Merge costsByCategory
            if (plan.costsByCategory) {
                Object.entries(plan.costsByCategory).forEach(([category, cost]) => {
                    if (!mergedPlan.costsByCategory) {
                        mergedPlan.costsByCategory = emptyCostsByCategory();
                    }
                    mergedPlan.costsByCategory[category as CostCategory] += cost;
                });
            }
        });
    });

    return mergedPlans;
};
